import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { API_BASE_URL } from '../config/api.config';
import { MAPBOX_TOKEN } from '../config/mapboxConfig';
import { getToken, getUserData } from '@/utils/storage';

// ─── Utilitaires GPS ──────────────────────────────────────────────────────────

function interpolatePoints(start, end, steps) {
  const pts = [];
  const dLat = (end.lat - start.lat) / steps;
  const dLon = (end.lon - start.lon) / steps;
  for (let i = 0; i <= steps; i++) {
    pts.push({ lat: start.lat + dLat * i, lon: start.lon + dLon * i });
  }
  return pts;
}

function smoothPath(pts, minDistM = 20) {
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i],
      p2 = pts[i + 1];
    out.push(p1);
    const dist = Math.sqrt((p2.lat - p1.lat) ** 2 + (p2.lon - p1.lon) ** 2) * 111000;
    if (dist > minDistM) {
      const seg = interpolatePoints(p1, p2, Math.floor(dist / minDistM));
      for (let j = 1; j < seg.length - 1; j++) out.push(seg[j]);
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

function calculateBearing(lat1, lon1, lat2, lon2) {
  const la1 = (lat1 * Math.PI) / 180,
    la2 = (lat2 * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function haversineMeters(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function generateCirclePath(cLat, cLon, radiusM, totalPoints, laps = 2) {
  const R = 6371000;
  const la = (cLat * Math.PI) / 180,
    lo = (cLon * Math.PI) / 180;
  return Array.from({ length: totalPoints }, (_, i) => {
    const angle = (i / totalPoints) * 2 * Math.PI * laps;
    const ad = radiusM / R;
    const lat2 = Math.asin(Math.sin(la) * Math.cos(ad) + Math.cos(la) * Math.sin(ad) * Math.cos(angle));
    const lon2 =
      lo + Math.atan2(Math.sin(angle) * Math.sin(ad) * Math.cos(la), Math.cos(ad) - Math.sin(la) * Math.sin(lat2));
    return { lat: (lat2 * 180) / Math.PI, lon: (lon2 * 180) / Math.PI };
  });
}

async function getMapboxRoute(stops) {
  if (!MAPBOX_TOKEN || stops.length < 2) return null;
  try {
    let pts = stops;
    if (stops.length > 25) {
      const mid = stops.slice(1, -1);
      const step = Math.ceil(mid.length / 23);
      pts = [stops[0], ...mid.filter((_, i) => i % step === 0), stops[stops.length - 1]];
    }
    const coords = pts.map((s) => `${s[1]},${s[0]}`).join(';');
    const approaches = pts.map(() => 'curb').join(';');
    const url =
      `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}` +
      `?geometries=geojson&overview=full&continue_straight=true&approaches=${approaches}` +
      `&access_token=${MAPBOX_TOKEN}`;
    const r = await fetch(url);
    const data = await r.json();
    if (data.routes?.[0]?.geometry?.coordinates) {
      return data.routes[0].geometry.coordinates.map((c) => ({ lat: c[1], lon: c[0] }));
    }
  } catch (_e) {
    /* fallback silencieux */
  }
  return null;
}

async function buildPathFromStops(stops) {
  const mapboxPts = await getMapboxRoute(stops);
  if (mapboxPts) return smoothPath(mapboxPts, 5);
  let path = [];
  for (let i = 0; i < stops.length - 1; i++) {
    path = path.concat(
      interpolatePoints({ lat: stops[i][0], lon: stops[i][1] }, { lat: stops[i + 1][0], lon: stops[i + 1][1] }, 20),
    );
  }
  return path;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `API ${path}: ${res.status}`);
  }
  return res.json();
};

const loadSchoolCoords = async () => {
  try {
    const raw = getUserData() || '{}';
    const userData = JSON.parse(raw);
    const school = userData?.user?.school || userData?.school;
    if (school?.latitude && school?.longitude) return [parseFloat(school.latitude), parseFloat(school.longitude)];
  } catch (_e) {
    /* fallback silencieux */
  }
  try {
    const r = await apiFetch('/admin/schools');
    const school = (r.data || [])[0];
    if (school?.latitude && school?.longitude) return [parseFloat(school.latitude), parseFloat(school.longitude)];
  } catch (_e) {
    /* fallback silencieux */
  }
  return null;
};

// ─── Context ──────────────────────────────────────────────────────────────────

const DevSimulatorContext = createContext(null);

export const DevSimulatorProvider = ({ children }) => {
  // ── Paramètres partagés ───────────────────────────────────────────────────
  const [buses, setBuses] = useState([]);
  const [selectedBus, setSelectedBus] = useState(null);
  const [scenario, setScenario] = useState('assigned_route');
  const [direction, setDirection] = useState('morning');
  const [speedKmh, setSpeedKmh] = useState(40);
  const [speedFactor, setSpeedFactor] = useState(1); // ×1 ×2 ×5 ×10
  const [simulatedTime, setSimulatedTime] = useState('');
  const [loading, setLoading] = useState(false);

  // ── État multi-bus ────────────────────────────────────────────────────────
  // activeSims : Map<busId, { bus, statsSent, elapsed, progress, logs }>
  const [activeSims, setActiveSims] = useState(new Map());

  // simsRef : Map<busId, { idx, bearing, stopState, stopHold, pathPoints,
  //                        stopCoords, tripType, timerRef }>
  const simsRef = useRef(new Map());

  const speedRef = useRef(speedKmh);
  const speedFactorRef = useRef(speedFactor);
  const simulatedTimeRef = useRef(simulatedTime);
  useEffect(() => {
    speedRef.current = speedKmh;
  }, [speedKmh]);
  useEffect(() => {
    speedFactorRef.current = speedFactor;
  }, [speedFactor]);
  useEffect(() => {
    simulatedTimeRef.current = simulatedTime;
  }, [simulatedTime]);

  const anyRunning = activeSims.size > 0;
  const totalSent = [...activeSims.values()].reduce((s, sim) => s + sim.statsSent, 0);

  // Charge les bus au montage — seulement si l'utilisateur est authentifié
  useEffect(() => {
    if (!getToken()) return;
    apiFetch('/admin/buses')
      .then((r) => setBuses(r.data || []))
      .catch(() => toast.error('Erreur chargement des bus'));
  }, []);

  // Chrono global : incrémente elapsed de toutes les sims actives chaque seconde
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => {
      setActiveSims((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        for (const [id, sim] of next) next.set(id, { ...sim, elapsed: sim.elapsed + 1 });
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [anyRunning]);

  // ── Helpers internes ──────────────────────────────────────────────────────

  const addLog = useCallback((busId, msg, type = 'info') => {
    const time = new Date().toLocaleTimeString('fr-FR');
    setActiveSims((prev) => {
      const next = new Map(prev);
      const sim = next.get(busId);
      if (!sim) return prev;
      next.set(busId, { ...sim, logs: [...sim.logs.slice(-149), { time, msg, type }] });
      return next;
    });
  }, []);

  const sendGps = useCallback((payload) => {
    const body = { ...payload };
    const st = simulatedTimeRef.current;
    if (st) body.simulated_time = st;
    apiFetch('/admin/dev/simulate-gps', { method: 'POST', body: JSON.stringify(body) }).catch(() => {}); // fire-and-forget silencieux
  }, []);

  const finishSimulation = useCallback(
    (busId, s) => {
      const lastPt = s.pathPoints[s.pathPoints.length - 1];
      sendGps({
        id: busId,
        lat: lastPt.lat,
        lon: lastPt.lon,
        speed: 0,
        heading: s.bearing,
        ignition: false,
        motion: false,
        event: 'update',
      });
      setTimeout(() => {
        sendGps({
          id: busId,
          lat: lastPt.lat,
          lon: lastPt.lon,
          speed: 0,
          heading: 0,
          ignition: false,
          motion: false,
          event: 'trip_ended',
        });
      }, 2000);
    },
    [sendGps],
  );

  // Crée le tick pour un bus donné.
  // Avancement basé sur la vitesse réelle : speedKmh × speedFactor mètres par tick.
  const createTick = useCallback(
    (busId) => () => {
      const s = simsRef.current.get(busId);
      if (!s) return;

      const speedFactor = speedFactorRef.current;
      const metersThisTick = ((speedRef.current * 1000) / 3600) * speedFactor;
      let currentSpeed = speedRef.current;
      let finished = false;

      if (s.stopState === 'at_stop') {
        // Consomme le hold proportionnellement au speedFactor
        s.stopHold -= speedFactor;
        currentSpeed = 0;
        if (s.stopHold <= 0) s.stopState = 'leaving';
      } else {
        // Accumule la distance et avance autant de points que nécessaire
        s.distAcc += metersThisTick;

        while (s.distAcc > 0) {
          if (s.idx >= s.pathPoints.length - 1) {
            finished = true;
            break;
          }

          const curr = s.pathPoints[s.idx];
          const next = s.pathPoints[s.idx + 1];
          const segDist = haversineMeters(curr.lat, curr.lon, next.lat, next.lon) || 1;

          if (s.distAcc < segDist) break; // pas assez pour atteindre le prochain point

          s.distAcc -= segDist;
          s.bearing = calculateBearing(curr.lat, curr.lon, next.lat, next.lon);
          s.idx++;

          const pt = s.pathPoints[s.idx];
          const nearStop = s.stopCoords?.some((c) => haversineMeters(pt.lat, pt.lon, c[0], c[1]) < 50);

          if (s.stopState === 'moving' && nearStop) {
            s.stopState = 'at_stop';
            s.stopHold = 8;
            s.distAcc = 0;
            currentSpeed = 0;
            break;
          }
          if (s.stopState === 'leaving' && !nearStop) {
            s.stopState = 'moving';
          }
        }
      }

      const point = s.pathPoints[Math.min(s.idx, s.pathPoints.length - 1)];

      sendGps({
        id: busId,
        lat: point.lat,
        lon: point.lon,
        speed: currentSpeed,
        heading: s.bearing,
        ignition: true,
        motion: currentSpeed > 0,
        event: 'update',
      });

      setActiveSims((prev) => {
        const next = new Map(prev);
        const sim = next.get(busId);
        if (!sim) return prev;
        next.set(busId, {
          ...sim,
          statsSent: sim.statsSent + 1,
          progress: { current: s.idx, total: s.pathPoints.length, stopState: s.stopState },
        });
        return next;
      });

      if (finished) {
        clearInterval(s.timerRef);
        simsRef.current.delete(busId);
        finishSimulation(busId, s);
        addLog(busId, 'Simulation terminée — TRIP_ENDED envoyé', 'success');
      }
    },
    [sendGps, finishSimulation, addLog],
  );

  // ── Actions publiques ─────────────────────────────────────────────────────

  const buildPath = async (bus, scen, dir) => {
    if (scen === 'service_school_loop') {
      addLog(bus.id, 'Chargement coordonnées école...', 'info');
      const coords = await loadSchoolCoords();
      if (!coords) throw new Error("L'école n'a pas de coordonnées GPS en base");
      const pts = generateCirclePath(coords[0], coords[1], 200, 180, 2);
      addLog(bus.id, `Circuit service: rayon 200 m, ${pts.length} points`, 'success');
      return { pathPoints: pts, stopCoords: null, tripType: 'service' };
    }

    addLog(bus.id, 'Recherche de la route assignée...', 'info');
    const routesR = await apiFetch('/admin/routes');
    const routes = routesR.data || [];
    const route = routes.find((r) => r.bus_id === bus.id || r.assigned_bus_id === bus.id);
    if (!route) throw new Error('Aucune route assignée à ce bus');

    addLog(bus.id, `Route: ${route.name || 'Sans nom'}`, 'success');
    const apiDir = dir === 'morning' ? 'inbound' : 'outbound';
    addLog(bus.id, `Chargement arrêts (${dir === 'morning' ? 'aller' : 'retour'})...`, 'info');
    const detailR = await apiFetch(`/admin/routes/${route.id}?direction=${apiDir}`);
    const detail = detailR.data || route;

    const routeStops = (detail.route_stops || [])
      .map((rs) =>
        rs.stop?.latitude && rs.stop?.longitude ? [parseFloat(rs.stop.latitude), parseFloat(rs.stop.longitude)] : null,
      )
      .filter(Boolean);

    if (routeStops.length < 2) throw new Error('Route avec moins de 2 arrêts valides');
    addLog(bus.id, `${routeStops.length} arrêts chargés`, 'info');

    const schoolCoords = await loadSchoolCoords();
    const stops = schoolCoords ? [schoolCoords, ...routeStops, schoolCoords] : routeStops;

    addLog(bus.id, `Calcul itinéraire Mapbox (${stops.length} points)...`, 'info');
    const pathPoints = await buildPathFromStops(stops);
    addLog(bus.id, `Itinéraire prêt: ${pathPoints.length} waypoints`, 'success');
    return { pathPoints, stopCoords: stops, tripType: dir };
  };

  const startBus = async () => {
    if (!selectedBus) {
      toast.error('Sélectionnez un bus');
      return;
    }
    if (simsRef.current.has(selectedBus.id)) {
      toast.error('Ce bus est déjà en cours de simulation');
      return;
    }

    // Initialise l'entrée display avant buildPath pour que les logs apparaissent
    setActiveSims((prev) => {
      const next = new Map(prev);
      next.set(selectedBus.id, {
        bus: selectedBus,
        statsSent: 0,
        elapsed: 0,
        progress: { current: 0, total: 0, stopState: 'moving' },
        logs: [],
      });
      return next;
    });

    setLoading(true);
    addLog(selectedBus.id, 'Initialisation...', 'info');
    let pathData;
    try {
      pathData = await buildPath(selectedBus, scenario, direction);
    } catch (e) {
      toast.error(e.message);
      addLog(selectedBus.id, `Erreur: ${e.message}`, 'error');
      setLoading(false);
      setActiveSims((prev) => {
        const next = new Map(prev);
        next.delete(selectedBus.id);
        return next;
      });
      return;
    }
    setLoading(false);

    const { pathPoints, stopCoords, tripType } = pathData;
    const busId = selectedBus.id;

    const simState = {
      idx: 0,
      bearing: 0,
      stopState: 'moving',
      stopHold: 0,
      distAcc: 0,
      pathPoints,
      stopCoords,
      tripType,
      timerRef: null,
    };
    simsRef.current.set(busId, simState);

    setActiveSims((prev) => {
      const next = new Map(prev);
      const existing = next.get(busId) || {};
      next.set(busId, { ...existing, progress: { current: 0, total: pathPoints.length, stopState: 'moving' } });
      return next;
    });

    addLog(busId, 'Simulation démarrée', 'success');
    const tick = createTick(busId);
    simState.timerRef = setInterval(tick, 1000);
  };

  const stopBus = useCallback((busId) => {
    const s = simsRef.current.get(busId);
    if (s) {
      clearInterval(s.timerRef);
      simsRef.current.delete(busId);
    }
    setActiveSims((prev) => {
      const next = new Map(prev);
      next.delete(busId);
      return next;
    });
    // Clôture le trajet actif côté backend (event=reset ferme le trip en DB)
    apiFetch('/admin/dev/reset-bus', { method: 'POST', body: JSON.stringify({ busId }) }).catch(() => {});
    toast.info('Simulation arrêtée');
  }, []);

  const stopAll = useCallback(() => {
    for (const [busId] of simsRef.current) stopBus(busId);
  }, [stopBus]);

  const resetBus = useCallback(async (busId) => {
    try {
      await apiFetch('/admin/dev/reset-bus', { method: 'POST', body: JSON.stringify({ busId }) });
      toast.success('Bus réinitialisé');
    } catch (e) {
      toast.error(`Reset échoué: ${e.message}`);
    }
  }, []);

  const resetAll = useCallback(async () => {
    try {
      const r = await apiFetch('/admin/dev/reset-all', { method: 'POST' });
      toast.success(`${r.reset || 0} bus réinitialisé(s)`);
    } catch (e) {
      toast.error(`Reset all échoué: ${e.message}`);
    }
  }, []);

  return (
    <DevSimulatorContext.Provider
      value={{
        buses,
        selectedBus,
        setSelectedBus,
        scenario,
        setScenario,
        direction,
        setDirection,
        speedKmh,
        setSpeedKmh,
        speedFactor,
        setSpeedFactor,
        simulatedTime,
        setSimulatedTime,
        loading,
        anyRunning,
        totalSent,
        activeSims,
        startBus,
        stopBus,
        stopAll,
        resetBus,
        resetAll,
      }}
    >
      {children}
    </DevSimulatorContext.Provider>
  );
};

export const useDevSimulator = () => {
  const ctx = useContext(DevSimulatorContext);
  if (!ctx) throw new Error('useDevSimulator must be used inside DevSimulatorProvider');
  return ctx;
};
